import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "sonner";
import Pagination from "../Pagination";
import "../Sales Person/CreateSalesPerson.css";
import LoadingScreen from "../Utils/LoadingScreen";
import { Trash2, X, RefreshCcw, Plus, ShoppingBag, Pen } from "lucide-react";
import AddProducts from "../SuperAdmin/AddProducts";
import EditProduct from "../SuperAdmin/EditProduct";

const Dashboard = () => {
  const { Products, loading, GetProducts, setProducts, adminId, api } =
    useContext(GlobalContext);

  const [selectedProduct, setSelectedProduct] = useState(null);

  const sessionAdminId = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData");
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return parsed?.adminMeta?.adminId ? String(parsed.adminMeta.adminId) : "";
    } catch {
      return "";
    }
  }, []);

  const effectiveAdminId = adminId || sessionAdminId;
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [search, setSearch] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const filteredProducts = useMemo(() => {
    if (!Products || Products.length === 0) return [];

    let filtered = [...Products];

    if (search.trim()) {
      const searchLower = search.toLowerCase().trim();
      filtered = filtered.filter((product) => {
        const firmName = (product.firm_name || "").toLowerCase();
        const productName = (product.product_name || "").toLowerCase();
        const website = (product.website_url || "").toLowerCase();
        const onboardingUrl = (product.onboarding_url || "").toLowerCase();
        const productId = String(product.pk_id || "");

        return (
          firmName.includes(searchLower) ||
          productName.includes(searchLower) ||
          website.includes(searchLower) ||
          onboardingUrl.includes(searchLower) ||
          productId.includes(searchLower)
        );
      });
    }

    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);

      filtered = filtered.filter((product) => {
        if (product.created_at) {
          const productDate = new Date(product.created_at);
          return productDate >= from && productDate <= to;
        }
        return true;
      });
    }

    return filtered;
  }, [Products, search, fromDate, toDate]);

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, fromDate, toDate]);

  const productOptions = useMemo(
    () =>
      (Products || []).map((p) => ({
        value: p.pk_id ?? p.pkId ?? p.id ?? "",
        label: p.product_name ?? p.name ?? "Unknown",
      })),
    [Products]
  );

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!effectiveAdminId || !productToDelete) {
      toast.error("Admin ID or Product ID not found!");
      return;
    }

    setDeleteLoading(true);
    try {
      const payload = {
        adminId: String(effectiveAdminId),
        productId: String(productToDelete.pk_id),
      };

      const res = await api.post(`/Admin/v1.0/deleteProduct`, payload);

      if (res?.data?.responseCode === "00") {
        const deletedProductId = String(productToDelete.pk_id);

        if (typeof setProducts === "function") {
          setProducts((prevProducts) =>
            prevProducts.filter(
              (product) => String(product.pk_id) !== deletedProductId
            )
          );
        }

        toast.success("Product deleted successfully!");
        setIsDeleteConfirmOpen(false);
        setProductToDelete(null);

        if (String(selectedProduct) === deletedProductId) {
          const remainingProducts = (Products || []).filter(
            (product) => String(product.pk_id) !== deletedProductId
          );
          setSelectedProduct(
            remainingProducts.length > 0 ? remainingProducts[0].pk_id : null
          );
        }
      } else {
        toast.error(res?.data?.responseMessage || "Failed to delete product");
      }
    } catch (error) {
      toast.error("Something went wrong!");
      console.error("Delete product error:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Add Product
  const addProductRef = useRef();
  const handleAddProductClick = () => {
    addProductRef.current?.openModal();
  };

  // Edit Product
  const editProductRef = useRef();
  const handleEditClick = (productId) => {
    editProductRef.current?.openEditModal(productId);
  };

  const handleRefresh = async () => {
    try {
      setPageLoading(true);
      if (typeof GetProducts === "function") {
        await GetProducts();
        toast.success("All Products refreshed");
      }
    } catch (error) {
      toast.error("Failed to refresh products!");
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (
      (!Products || Products.length === 0) &&
      typeof GetProducts === "function"
    ) {
      GetProducts();
    }
  }, []);

  useEffect(() => {
    if (Products && Products.length > 0 && !selectedProduct) {
      setSelectedProduct(Products[0].pk_id);
    }
  }, [Products]);

  if (loading) {
    return (
      <div className="relative min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <LoadingScreen />
      </div>
    );
  }

  return (
    <div className="aoc-csp-screen w-full min-h-[90svh] bg-gradient-to-b from-gray-50 to-white shadow rounded-2xl">
      <div className="aoc-csp-container w-full px-4 py-6 md:py-4">
        <div className="aoc-csp-header flex items-center justify-between gap-4 mb-6 md:mb-10 animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="aoc-csp-badge flex items-center justify-center h-12 w-12 rounded-2xl bg-blue-100 shadow-sm">
              <ShoppingBag className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 tracking-tight">
                All Products
              </h1>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-end gap-4">
            <div className="min-w-[200px]">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products..."
                className="aoc-csp-control-new w-full"
              />
            </div>
            <div className="flex items-center gap-3">
              <div>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="custom-date-input bg-white dark:bg-blue-800 dark:text-gray-100"
                />
              </div>
              <div>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="custom-date-input bg-white dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
            </div>

            <div className="flex items-end gap-3 flex-wrap">
              <button className="btn btn-refresh" onClick={handleRefresh}>
                <RefreshCcw size={14} />
                <span>Refresh</span>
              </button>

              <div className="min-w-[100px]">
                <label className="aoc-csp-label ml-2">Product</label>
                <div className="aoc-csp-control-new">
                  <select
                    className="aoc-csp-input w-[100px] text-sm rounded outline-none"
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                  >
                    <option value="">Select product</option>
                    {productOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleAddProductClick}
                  className="aoc-csp-btn-add"
                >
                  <Plus className="h-4 w-4" />
                  Add Product
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="aoc-csp-card relative rounded-2xl animate-rise">
          <div className="aoc-csp-table-wrapper overflow-x-auto">
            <table className="aoc-csp-table w-full">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    S.NO
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Firm Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gst Image
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pan Image
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Website
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    OnBoarding Url
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Icons
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedProducts && paginatedProducts.length > 0 ? (
                  paginatedProducts.map((product, index) => (
                    <tr
                      key={product.pk_id}
                      className={`hover:bg-gray-50 transition-colors duration-150 ${
                        selectedProduct === product.pk_id ? "bg-blue-50" : ""
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {product.firm_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {product.product_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.gst_photo ? (
                          <img
                            src={product.gst_photo}
                            alt={product.product_name}
                            className="h-10 w-10 rounded-full mr-3 object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                            <span className="text-blue-600 font-semibold">
                              {product.product_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.pan_photo ? (
                          <img
                            src={product.pan_photo}
                            alt={product.product_name}
                            className="h-10 w-10 rounded-full mr-3 object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                            <span className="text-blue-600 font-semibold">
                              {product.product_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {product.website_url ? (
                          <a
                            href={product.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            Visit Website
                          </a>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {product.onboarding_url ? (
                          <a
                            href={product.onboarding_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            Onboarding Url
                          </a>
                        ) : (
                          <span className="text-gray-400 mx-7">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.icon_url ? (
                          <img
                            src={product.icon_url}
                            alt={product.product_name}
                            className="h-10 w-10 rounded-full mr-3 object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                            <span className="text-blue-600 font-semibold">
                              {product.product_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleEditClick(product.pk_id)}
                            className="aoc-csp-icon-btn text-blue-600 hover:text-blue-800"
                            title="Edit"
                          >
                            <Pen className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(product)}
                            className="aoc-csp-icon-btn text-red-600 hover:text-red-700"
                            title="Delete"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <svg
                          className="w-16 h-16 text-gray-300 mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                          />
                        </svg>
                        <p className="text-gray-500 text-lg font-medium">
                          {search || fromDate !== today || toDate !== today
                            ? "No products match your search"
                            : "No products found"}
                        </p>
                        <p className="text-gray-400 text-sm mt-1">
                          {search || fromDate !== today || toDate !== today
                            ? "Try adjusting your filters"
                            : "Start by adding your first product"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Edit Product Modal */}
        <EditProduct ref={editProductRef} />

        {/* Delete Confirmation Modal */}
        {isDeleteConfirmOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
              <div className="bg-gradient-to-r from-red-600 to-red-800 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Delete Product</h2>
                <button
                  onClick={() => setIsDeleteConfirmOpen(false)}
                  className="text-white hover:text-gray-200 text-2xl"
                >
                  <X />
                </button>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-2">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold">
                    {productToDelete?.product_name}
                  </span>
                  ?
                </p>
                <p className="text-gray-500 text-sm mb-6">
                  This action cannot be undone. All associated data will be
                  permanently removed.
                </p>

                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => setIsDeleteConfirmOpen(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={deleteLoading}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {deleteLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Deleting...
                      </>
                    ) : (
                      "Delete Product"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Product Modal */}
        <AddProducts ref={addProductRef} />
      </div>
    </div>
  );
};

export default Dashboard;
